import React, { useState, useEffect, useMemo } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Box,
} from '@mui/material';
import { 
  Menu as MenuIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Article as ArticleIcon,
  School as SchoolIcon,
  ThumbUp as ThumbUpIcon,
} from '@mui/icons-material';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const menuItems = useMemo(() => [
    { text: 'Home', href: '#home', icon: <HomeIcon /> },
    { text: 'I am..', href: '#iam', icon: <PersonIcon /> },
    { text: 'Experience', href: '#experience', icon: <WorkIcon /> },
    { text: 'Publications', href: '#publications', icon: <ArticleIcon /> },
    { text: 'Learnings', href: '#learnings', icon: <SchoolIcon /> },
    { text: 'Recommendations', href: '#recommendations', icon: <ThumbUpIcon /> },
  ], []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = menuItems.map(item => document.querySelector(item.href));
      const scrollPosition = window.pageYOffset;

      sections.forEach((section, index) => {
        if (section) {
          const sectionTop = (section as HTMLElement).offsetTop - 100;
          const sectionHeight = section.clientHeight;
          if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
            setActiveSection(menuItems[index].href.substring(1));
          }
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [menuItems]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      const headerHeight = 64; // Approximate header height
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerHeight - 20; // Additional 20px padding
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileOpen(false);
  };

  const drawer = (
    <Box sx={{ width: { xs: 260, sm: 280 }, py: { xs: 1.5, md: 2 } }}>
      <List sx={{ px: { xs: 1.5, md: 2 } }}>
        {menuItems.map((item, index) => {
          const isActive = activeSection === item.href.substring(1);
          return (
            <ListItem
              key={item.text}
              onClick={() => scrollToSection(item.href)}
              sx={{
                cursor: 'pointer',
                borderRadius: '12px',
                mb: { xs: 0.75, md: 1 },
                px: { xs: 1.5, md: 2 },
                py: { xs: 1.25, md: 1.5 },
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: isActive 
                  ? theme.palette.mode === 'dark'
                    ? 'rgba(0, 0, 0, 0.4)'
                    : 'rgba(0, 0, 0, 0.08)'
                  : 'transparent',
                border: isActive 
                  ? theme.palette.mode === 'dark'
                    ? '1px solid rgba(0, 0, 0, 0.5)'
                    : '1px solid rgba(0, 0, 0, 0.15)'
                  : '1px solid transparent',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: theme.palette.mode === 'dark'
                    ? 'rgba(0, 0, 0, 0.1)'
                    : 'rgba(0, 0, 0, 0.05)',
                  opacity: 0,
                  transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRadius: '12px',
                },
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(0, 0, 0, 0.3)'
                    : 'rgba(0, 0, 0, 0.06)',
                  transform: 'translateX(4px)',
                  '&::before': {
                    opacity: 1,
                  },
                  '& .MuiListItemIcon-root svg': {
                    transform: 'scale(1.1)',
                    color: theme.palette.text.primary,
                  }
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {React.cloneElement(item.icon, { 
                  sx: { 
                    fontSize: '1.2rem',
                    color: isActive 
                      ? (theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary)
                      : theme.palette.text.secondary,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' 
                  } 
                })}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    sx={{
                      fontSize: '1rem',
                      fontWeight: isActive ? 600 : 500,
                      fontFamily: '"Roboto Mono", "Courier New", monospace',
                      color: isActive 
                        ? theme.palette.text.primary
                        : theme.palette.text.secondary,
                      letterSpacing: '0.02em',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {item.text}
                  </Typography>
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: theme.palette.mode === 'dark' 
            ? 'rgba(18, 18, 18, 0.95)' 
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 2px 20px rgba(0, 0, 0, 0.3)'
            : '0 2px 20px rgba(0, 0, 0, 0.1)',
          borderBottom: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 400,
                color: theme.palette.text.primary,
                fontFamily: 'Allura, cursive !important',
                fontSize: { xs: '20px !important', sm: '22px !important', md: '24px !important' },
                lineHeight: 1.1,
                letterSpacing: '0.02em',
                textShadow: theme.palette.mode === 'dark' 
                  ? '0 2px 8px rgba(0,0,0,0.3)' 
                  : '0 2px 8px rgba(0,0,0,0.1)',
                fontStyle: 'normal',
                textTransform: 'none',
                '@media (min-width: 480px) and (max-width: 600px)': {
                  fontSize: '24px !important',
                },
                '@media (min-width: 601px) and (max-width: 960px)': {
                  fontSize: '28px !important',
                },
                '@media (min-width: 961px) and (max-width: 1280px)': {
                  fontSize: '32px !important',
                },
                '@media (min-width: 1281px)': {
                  fontSize: '36px !important',
                },
              }}
            >
              Priyakant Charokar
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isMobile ? (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ ml: 1, color: 'text.primary' }}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {menuItems.map((item) => {
                  const isActive = activeSection === item.href.substring(1);
                  return (
                    <Button
                      key={item.text}
                      onClick={() => scrollToSection(item.href)}
                      startIcon={React.cloneElement(item.icon, { 
                        sx: { 
                          fontSize: '1.1rem',
                          color: isActive ? theme.palette.text.primary : theme.palette.text.secondary, 
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)' 
                        } 
                      })}
                      sx={{
                        fontWeight: isActive ? 600 : 500,
                        fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.95rem' },
                        fontFamily: '"Roboto Mono", "Courier New", monospace',
                        px: { xs: 2, sm: 2.5, md: 3 },
                        py: { xs: 1.25, sm: 1.375, md: 1.5 },
                        borderRadius: '16px',
                        minHeight: { xs: '44px', md: '48px' },
                        position: 'relative',
                        overflow: 'hidden',
                        textTransform: 'none',
                        letterSpacing: '0.02em',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        backgroundColor: isActive 
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(0, 0, 0, 0.4)'
                            : 'rgba(0, 0, 0, 0.08)'
                          : 'transparent',
                        border: isActive 
                          ? theme.palette.mode === 'dark'
                            ? '1px solid rgba(0, 0, 0, 0.5)'
                            : '1px solid rgba(0, 0, 0, 0.2)'
                          : '1px solid transparent',
                        color: isActive 
                          ? theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary
                          : theme.palette.text.secondary,
                        boxShadow: isActive 
                          ? theme.palette.mode === 'dark'
                            ? '0 4px 20px rgba(0, 0, 0, 0.5)'
                            : '0 4px 20px rgba(0, 0, 0, 0.15)'
                          : 'none',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: theme.palette.mode === 'dark'
                            ? 'rgba(0, 0, 0, 0.1)'
                            : 'rgba(0, 0, 0, 0.05)',
                          opacity: 0,
                          transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          borderRadius: '16px',
                        },
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(0, 0, 0, 0.3)'
                            : 'rgba(0, 0, 0, 0.06)',
                          transform: 'translateY(-3px)',
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0 8px 32px rgba(0, 0, 0, 0.6)'
                            : '0 8px 32px rgba(0, 0, 0, 0.2)',
                          border: theme.palette.mode === 'dark'
                            ? '1px solid rgba(0, 0, 0, 0.6)'
                            : '1px solid rgba(0, 0, 0, 0.3)',
                          '&::before': {
                            opacity: 1,
                          },
                          '& .MuiButton-startIcon svg': {
                            color: theme.palette.text.primary,
                            transform: 'scale(1.1)',
                          },
                          '& .MuiButton-startIcon': {
                            transform: 'translateX(-2px)',
                          }
                        },
                        '&:active': {
                          transform: 'translateY(-1px)',
                        }
                      }}
                    >
                      {item.text}
                    </Button>
                  );
                })}
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            backgroundColor: theme.palette.background.paper,
            borderLeft: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header; 